import { animate } from '@angular/animations';
import { AfterViewInit, Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import * as THREE from 'three';

@Component({
  selector: 'app-road-background',
  templateUrl: './road-background.component.html',
  styleUrl: './road-background.component.scss'
})

export class RoadBackgroundComponent implements AfterViewInit {
  @ViewChild('canvasContainer', { static: true }) canvasRef!: ElementRef<HTMLElement>;

  private CONFIG = {
      color: 0x0165F4, // New Bright Blue
      bgColor: 0x0A0E1A, // Updated Background Color
      particleSize: 0.15,
      globeRadius: 11,
      rotationSpeed: 0.0005,
      lineColor: 0x0165F4, // Matching Blue
      showLines: true,

      // View parameters (kept from previous version)
      scale: 1.7,
      posX: 0,
      posY: -1,
      angleX: 0.7,
      angleZ: 0,
      isRecording: false,
      videoQuality: 5000000,
    };

    private scene: any;
    private camera: any;
    private renderer: any;
    private earthGroup: any;
    private spinGroup: any;
    private stars: any;
    private earthPointsMesh: any;

    private textureLoader = new THREE.TextureLoader();


  @HostListener('window:resize')
  resizeCanvas(): void {
      this.camera = new THREE.PerspectiveCamera(45, (window.innerWidth / window.innerHeight), 0.1, 1000);
      this.camera.position.set(0, 10, 35);
      this.renderer.setSize(window.innerWidth, window.innerHeight * 1.1);
  }

  ngAfterViewInit(): void {
    this.textureLoader.setCrossOrigin('anonymous');
    this.init();
  }

    private init() {
      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(this.CONFIG.bgColor);
      this.scene.fog = new THREE.FogExp2(this.CONFIG.bgColor, 0.02);

      this.camera = new THREE.PerspectiveCamera(45, (window.innerWidth  / window.innerHeight), 0.1, 1000);
      this.camera.position.set(0, 10, 35);

      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
      this.renderer.setSize(window.innerWidth, window.innerHeight * 1.1);
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      this.canvasRef.nativeElement.appendChild(this.renderer.domElement);

      this.createUniverse();
      this.animate();
    }

    private createUniverse() {
      // Earth Group handles Position, Scale, and TILT
      this.earthGroup = new THREE.Group();
      this.earthGroup.scale.set(this.CONFIG.scale, this.CONFIG.scale, this.CONFIG.scale);
      this.earthGroup.position.set(this.CONFIG.posX, this.CONFIG.posY, 0);
      this.earthGroup.rotation.x = this.CONFIG.angleX;
      this.earthGroup.rotation.z = this.CONFIG.angleZ;
      this.scene.add(this.earthGroup);

      // Spin Group handles Rotation (Spinning)
      // It is a child of earthGroup, so it tilts with it
      this.spinGroup = new THREE.Group();
      this.earthGroup.add(this.spinGroup);

      const earthMapUrl = 'https://raw.githubusercontent.com/mrdoob/three.js/master/examples/textures/planets/earth_specular_2048.jpg';

      this.textureLoader.load(earthMapUrl, (texture: any) => {
        this.generateEarthPoints(texture);
      }, undefined, (err: any) => {
      });


    }

     generateEarthPoints(texture: any) {
      const img = texture.image;
      const canvas = document.createElement('canvas');
      canvas.width = 200;
      canvas.height = 100;
      const ctx: any = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imgData.data;

      const positions = [];
      const colors = [];

      for (let y = 0; y < canvas.height; y++) {
        for (let x = 0; x < canvas.width; x++) {
          const index = (y * canvas.width + x) * 4;
          const r = data?.[index];
          if (r > 50) {
            const lat = (y / canvas.height) * 180 - 90;
            const lon = (x / canvas.width) * 360 - 180;
            const phi = (90 - lat) * (Math.PI / 180);
            const theta = (lon + 180) * (Math.PI / 180);
            const vx = -this.CONFIG.globeRadius * Math.sin(phi) * Math.cos(theta);
            const vy = this.CONFIG.globeRadius * Math.cos(phi);
            const vz = this.CONFIG.globeRadius * Math.sin(phi) * Math.sin(theta);
            positions.push(vx, vy, vz);
            const brightness = 0.5 + Math.random() * 0.5;
            colors.push(brightness, brightness, brightness);
          }
        }
      }

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

      const material = new THREE.PointsMaterial({
        size: this.CONFIG.particleSize,
        color: this.CONFIG.color,
        vertexColors: true,
        transparent: false,
        opacity: 0.4,
        sizeAttenuation: true,
        blending: THREE.AdditiveBlending
      });

      this.earthPointsMesh = new THREE.Points(geometry, material);
      // Add to spinGroup
      this.spinGroup.add(this.earthPointsMesh);
    }

    private animate() {
      requestAnimationFrame(this.animate.bind(this));
      // Spin the SPIN group (around local Y axis)
      if (this.spinGroup) this.spinGroup.rotation.y += this.CONFIG.rotationSpeed;

      if (this.stars) {
        this.stars.rotation.y += this.CONFIG.rotationSpeed * 0.1;
        this.stars.rotation.x += this.CONFIG.rotationSpeed * 0.05;
      }
      this.renderer.render(this.scene, this.camera);
    }

}
